import type { Me } from "../shell/types";
import { api, el, tipHide, toast } from "../ui/dom";
import { icon } from "../ui/icons";
import { poweredBy } from "../ui/powered";
import { chip } from "../ui/record";
import { uploadFile } from "../ui/upload";
import { photoImg } from "../ui/face";

// The caregiver's field app (brief 1D-3, mockup v41 Field app): the same app on the phone, five
// tabs (Care, Visit, Schedule, Documents, Chat), the header with the company and the caregiver,
// and the menu sheet. A caregiver sees only their own published visits; drafts never reach here.
// - Care: the visit now or next (Open visit · check in), the rest of today, the week.
// - Visit: 1 Check in (selfie and location; never refused, only flagged; with no signal it's kept
//   on the phone and sent once back online, marked sent late with the phone's time), 2 the care
//   plan's tasks (Done, Not done, Refused), the note for the office (dictation when it's on), 3
//   the sign-off the client asks for, mileage with a receipt photo, and Check out and submit,
//   which always works: what's missing goes to the office as flags.
// - Schedule: Please answer (Confirm, Can't work with why; inside the cut-off "call the office"),
//   Confirmed, Open visits near you (Claim: the office approves), My availability and Time off.
// - Documents: their credentials; an upload waits in Inbox › Documents for review.
// - Chat: their conversation with the office.
// Brief 1E-18 (mockup v76, v78): Running late? on the Care tab (a text to the patient with an ETA
// once the start has passed with no check-in and the phone is away), the orientation checklist
// each person checks on their own phone, Before you leave before check-out (what's coming for the
// patient, Told the patient), and a note changed after check-out asks why (a late entry).

type Tab = "care" | "visit" | "schedule" | "docs" | "chat";
const TABS: [Tab, string, string][] = [
  ["care", "heart", "Care"],
  ["visit", "checklist", "Visit"],
  ["schedule", "calendar", "Schedule"],
  ["docs", "file", "Documents"],
  ["chat", "comment", "Chat"],
];

interface Card {
  id: string;
  day: string;
  date: string;
  weekday: string;
  start: string;
  end: string;
  hours: string;
  startsAt: string;
  patientFirst: string;
  service: string;
  status: string;
  confirm: string | null;
  cannotWorkReason: string | null;
  checkedIn: string | null;
  checkedOut: string | null;
  /** The patient's photo (brief 1E-17). */
  patientPhoto?: string | null;
}
/** Before you start (brief 1E-17, mockup v76): a caregiver new to the patient, or back after a while. */
interface BeforeYouStart {
  visitId: string;
  first: string;
  words: string;
  read: boolean;
  highlights: { id: string; body: string; photoFileId: string | null; marked: boolean }[];
  talk: string[];
  items: { id: string; body: string; section: string; marked: boolean }[];
}
/** An orientation checklist this caregiver checks (brief 1E-18): as the new caregiver or the one orienting. */
interface OrientationCheck {
  id: string;
  patientFirst: string;
  visitId: string | null;
  status: string;
  chip: { words: string; level: "ok" | "warn" | "info" };
  who: string;
  by: string;
  date: string;
  side: "orienting" | "new" | null;
  canCheck: boolean;
  items: {
    id: string;
    body: string;
    orienting: boolean;
    new: boolean;
    agreed: boolean;
    orientingComment: string;
    newComment: string;
  }[];
}
interface Home {
  company: string;
  /** Brief 1E-34: the office's messages since she last opened Chat. */
  chatUnread: number;
  person: { id: string; name: string; first: string; title: string | null };
  today: string;
  now: Card | null;
  next: Card | null;
  rest: Card[];
  week: { visits: number; done: number; hours: number };
  toAnswer: number;
  beforeYouStart: BeforeYouStart | null;
  orientations?: OrientationCheck[];
  /** Brief 1E-19: appointments during care this caregiver is asked about. */
  asks?: Ask[];
  /** Call before you leave on the next visit (not checked in yet). */
  callBefore?: { visitId: string; done: string | null } | null;
}
/** An appointment during one of this caregiver's visits (brief 1E-19, mockup v79). */
interface Ask {
  id: string;
  visitId: string;
  title: string;
  words: string;
  choices: { value: string; label: string }[];
  answer: string | null;
}
interface AnswerCard extends Card {
  canSayCantWork: boolean;
  callOffice: boolean;
  callWords: string | null;
}
/** Brief 1E-33: her worked visits, read-only. */
interface PastVisit {
  id: string;
  when: string;
  patient: string;
  hours: string;
  tasks: string | null;
  note: string | null;
  flags: string[];
  status: { text: string; level: string };
}
interface FieldEarnings {
  rows: {
    period: string;
    visits: number;
    hours: string;
    gross: string;
    status: { text: string; level: string };
  }[];
}
interface Sched {
  past: PastVisit[];
  toAnswer: AnswerCard[];
  confirmed: AnswerCard[];
  cantWork: AnswerCard[];
  open: (Card & { miles: number | null; approx?: boolean; claimed: boolean })[];
  claiming: boolean;
  reasons: string[];
  phone: string;
}
interface Punch {
  at: string;
  distance: string | null;
  selfie: boolean;
  sentLate: boolean;
  flags: { key: string; words: string }[];
}
interface VisitView {
  visit: Card;
  patient: { first: string; address: string; onMap: boolean; photoFileId: string | null } | null;
  checkIn: {
    opensAt: string;
    open: boolean;
    in: Punch | null;
    out: Punch | null;
    /** When the caregiver checked in (the clock counts on from it), and the scheduled hours. */
    since: string | null;
    scheduled: string;
  };
  /** The visit's day and hours, and the parts of the day they cover (brief 1E-10). */
  dueWords: string;
  tasks: {
    id: string;
    task: string;
    label: string;
    extra: boolean;
    result: string | null;
    note: string | null;
  }[];
  otherTasks: { id: string; task: string; next: string }[];
  alsoDone: string;
  results: { value: string; label: string }[];
  note: { body: string; dictated: boolean };
  signoff: {
    rule: string;
    choices: { value: string; label: string }[];
    done: { kind: string; label: string; signer: string | null; at: string } | null;
  };
  mileage: { miles: number; purpose: string | null; receipt: boolean } | null;
  missing: string[];
  checkoutWords: string;
  /** Sent back by the office with what to fix (brief 1D-4). */
  sentBack: { note: string; at: string } | null;
  /** Brief 1E-18: a late entry, whether a change now needs a reason, the late text, Before you leave. */
  noteLate?: { words: string } | null;
  noteNeedsReason?: boolean;
  lateText?: { words: string } | null;
  beforeYouLeave?: {
    items: { icon: string; when: string; what: string }[];
    told: { at: string } | null;
  } | null;
}
interface DaySetting {
  kind: "available" | "prefers" | "unavailable" | null;
  start: string;
  end: string;
}
interface Avail {
  grid: DaySetting[];
  asked: DaySetting[] | null;
  kinds: { value: string; label: string }[];
  days: string[];
  /** Weekday numbers in the order the company's week runs (brief 1D-5). */
  order?: number[];
  lockHours: number;
  timeOff: {
    id: string;
    words: string;
    reason: string | null;
    status: string;
    note: string | null;
    /** "16 h · 14.5 h paid" (brief 1E-11). */
    hours?: string | null;
  }[];
  /** The person's own policies with what's left (brief 1E-11). */
  pto?: {
    policies: {
      id: string;
      name: string;
      paid: boolean;
      left: number;
      words: string;
      usable: boolean;
      usableWords: string | null;
    }[];
    hoursADay: number;
  };
  waiting?: boolean;
}

/** "14.5", "16". */
const hrs = (n: number) => String(Number(n.toFixed(2)));

/** "16 h asked: 14.5 h paid, 1.5 h unpaid" (server/rules/pto.ts splitWords). */
function splitLine(asked: number, left: number | null): string {
  const a = `${hrs(asked)} h asked`;
  if (left === null) return `${a}, unpaid`;
  const paid = Math.max(0, Math.min(left, asked));
  if (!paid) return `${a}, unpaid`;
  if (paid >= asked) return `${a}, all paid`;
  return `${a}: ${hrs(paid)} h paid, ${hrs(asked - paid)} h unpaid`;
}

const when = (c: Card, today: string) =>
  c.day === today ? c.hours : `${c.weekday} ${c.date.slice(0, 5)} · ${c.hours}`;

const STATE: Record<string, [string, string]> = {
  in_progress: ["Checked in", "info"],
  completed: ["Checked out", "ok"],
  documented: ["Checked out", "ok"],
  validated: ["Checked out", "ok"],
};
const stateChip = (c: Card) => {
  const s = STATE[c.status];
  if (s)
    return chip(
      c.status === "in_progress" && c.checkedIn ? `Checked in ${c.checkedIn}` : s[0],
      s[1],
    );
  if (c.confirm === "confirmed") return chip("Confirmed", "ok");
  if (c.confirm === "cannot_work") return chip("Can't work", "bad");
  return chip("Please answer", "warn");
};

/** A big phone button (mockup pbtn). */
function pbtn(label: (Node | string)[], run: () => void, attrs: Record<string, string> = {}) {
  const b = el("button", { type: "button", class: "pbtn", ...attrs }, ...label);
  b.addEventListener("click", run);
  return b;
}

// ---- Kept on the phone without signal (brief 1D-3) ---------------------------------------------------

const queueKey = (me: Me) => `fc-field-queue:${me.user.id}`;
function queued(me: Me): { path: string; body: Record<string, unknown> }[] {
  try {
    return JSON.parse(localStorage.getItem(queueKey(me)) ?? "[]") as {
      path: string;
      body: Record<string, unknown>;
    }[];
  } catch {
    return [];
  }
}
function keep(me: Me, list: { path: string; body: Record<string, unknown> }[]) {
  try {
    localStorage.setItem(queueKey(me), JSON.stringify(list));
  } catch {
    // A private window may refuse storage; the punch then waits only while the page is open.
  }
}
/** Sends what was kept; answers how many went. */
async function flush(me: Me): Promise<number> {
  let sent = 0;
  for (const q of queued(me)) {
    try {
      const res = await api("POST", q.path, q.body);
      if (res.status >= 500) break;
      keep(
        me,
        queued(me).filter(
          (x) => x.path !== q.path || JSON.stringify(x.body) !== JSON.stringify(q.body),
        ),
      );
      sent++;
    } catch {
      break;
    }
  }
  return sent;
}

/** Where the phone is, or null (no permission, no fix in 10 seconds). */
function locate(): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 30_000 },
    );
  });
}

// ---- The app ---------------------------------------------------------------------------------------------

export async function fieldApp(
  host: HTMLElement,
  ctx: { me: Me; go: (path: string) => void; office: boolean },
) {
  const { me, go } = ctx;
  const parts = location.pathname.split("/").filter(Boolean);
  const tab = (TABS.find((t) => t[0] === parts[1])?.[0] ?? "care") as Tab;
  const visitId = parts[2] ?? null;

  const homeRes = await api<Home>("GET", "/api/field");
  const homeData = homeRes.ok ? homeRes.body : null;
  const body = el("div", { class: "pbody", id: "main" });
  const tabs = el(
    "nav",
    { class: "ptabs", "aria-label": "App" },
    ...TABS.map(([k, ic, l]) => {
      // Brief 1E-34: Chat shows how many of the office's messages came since she last opened it.
      const unread = k === "chat" && tab !== "chat" ? (homeData?.chatUnread ?? 0) : 0;
      const b = el(
        "button",
        {
          type: "button",
          "aria-current": k === tab ? "true" : "false",
          ...(unread ? { "aria-label": `${l}, ${unread} unread` } : {}),
        },
        icon(ic),
        el("span", {}, l),
        unread
          ? el(
              "span",
              { class: "punread", "aria-hidden": "true" },
              unread > 9 ? "9+" : String(unread),
            )
          : null,
      );
      b.addEventListener("click", () => go(`/field/${k}`));
      return b;
    }),
  );
  const menuBtn = el(
    "button",
    {
      type: "button",
      class: "phq",
      "aria-label": "Account menu",
      "data-tip": "Menu",
      "aria-haspopup": "menu",
    },
    icon("three-bars"),
  );
  const shell = el(
    "div",
    { class: "papp live", "data-app": "field" },
    el(
      "header",
      { class: "phd" },
      el(
        "div",
        { class: "phd-t" },
        el("b", {}, homeData?.company ?? me.company.name),
        el(
          "span",
          {},
          el("i", { class: "pdot" }),
          homeData
            ? `${homeData.person.name}${homeData.person.title ? ` · ${homeData.person.title}` : ""}`
            : me.user.name,
        ),
      ),
      el(
        "div",
        { class: "phd-r" },
        ctx.office
          ? (() => {
              const b = el(
                "button",
                {
                  type: "button",
                  class: "phq",
                  "aria-label": "Office",
                  "data-tip": "Back to the office screens",
                },
                icon("organization"),
              );
              b.addEventListener("click", () => go("/"));
              return b;
            })()
          : null,
        menuBtn,
      ),
    ),
    body,
    tabs,
  );
  host.replaceChildren(shell);
  menuBtn.addEventListener("click", () => openMenu(shell, me, ctx));

  // What was kept on the phone goes once there's signal again.
  const sendKept = async () => {
    const n = await flush(me);
    if (n) {
      toast("Sent", `${n} kept on this phone went to the office`);
      if (shell.isConnected) go(location.pathname);
    }
  };
  window.addEventListener("online", () => void sendKept(), { once: true });
  if (queued(me).length && navigator.onLine) void sendKept();

  if (!homeData) {
    body.append(
      el(
        "div",
        { class: "pempty", role: "alert" },
        icon("circle-slash", "big"),
        el("h2", {}, "Not available"),
        el("p", { class: "muted" }, homeRes.body.error ?? "The field app couldn't be loaded."),
      ),
    );
    return;
  }
  const h = homeData;
  if (tab === "care") careTab(shell, body, h, go);
  if (tab === "visit") await visitTab(body, h, me, go, visitId ?? h.now?.id ?? h.next?.id ?? null);
  if (tab === "schedule") await scheduleTab(body, shell, h, go);
  if (tab === "docs") await docsTab(body);
  if (tab === "chat") await chatTab(body);
}

// ---- The menu sheet ------------------------------------------------------------------------------------------

let installPrompt: (Event & { prompt: () => Promise<void> }) | null = null;
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  installPrompt = e as Event & { prompt: () => Promise<void> };
});

function openMenu(shell: HTMLElement, me: Me, ctx: { go: (p: string) => void; office: boolean }) {
  tipHide();
  const close = () => {
    scrim.remove();
    sheet.remove();
    document.removeEventListener("keydown", onKey);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
  };
  const item = (ic: string, label: string, run: () => void) => {
    const b = el(
      "button",
      { type: "button", class: "psh-item", role: "menuitem" },
      icon(ic),
      ` ${label}`,
      icon("chevron-right"),
    );
    b.addEventListener("click", () => {
      close();
      run();
    });
    return b;
  };
  const scrim = el("div", { class: "psheet-scrim" });
  scrim.addEventListener("click", close);
  const sheet = el(
    "div",
    { class: "psheet", role: "menu", "aria-label": "Account menu" },
    el("div", { class: "psh-grab" }),
    el("div", { class: "amlab" }, me.user.name),
    ...(ctx.office ? [item("organization", "Office screens", () => ctx.go("/"))] : []),
    item("download", "Add to home screen", () => {
      if (installPrompt) void installPrompt.prompt();
      else
        toast(
          "Add to home screen",
          "In the browser's menu, pick Add to Home Screen (iPhone: Share, then Add to Home Screen)",
        );
    }),
    item("sign-out", "Sign out", async () => {
      await fetch("/api/auth/signout", { method: "POST" }).catch(() => undefined);
      location.assign("/");
    }),
    poweredBy("p"),
  );
  shell.append(scrim, sheet);
  document.addEventListener("keydown", onKey);
  (sheet.querySelector("button") as HTMLElement | null)?.focus();
}

// ---- Care ----------------------------------------------------------------------------------------------------

/** The patient's photo on a visit, from the caregiver's own visit. */
const fieldPhoto = (visitId: string, fileId: string, alt: string) =>
  photoImg(`/api/field/visits/${visitId}/photos/${fileId}`, alt, "pav big");

/** Who the visit is with: the photo, when there is one, and the first name. */
const withWhom = (visitId: string, first: string, photo: string | null | undefined) =>
  photo
    ? el(
        "div",
        { class: "nv-face" },
        fieldPhoto(visitId, photo, `Photo of ${first}`),
        el("h2", { style: "margin:0" }, first),
      )
    : el("h2", { style: "margin:0" }, first);

/**
 * Before you start (brief 1E-17, mockup v76, v78's label): up to four highlights with their photos,
 * the conversation starters, I've read this (in the visit's history) and Something changed (marks an item for
 * the nurse).
 */
function beforeYouStartCard(shell: HTMLElement, first: BeforeYouStart): HTMLElement {
  let b = first;
  const card = el("section", { class: "pcard", "aria-label": "Before you start" });
  const base = `/api/field/visits/${b.visitId}`;
  const draw = () => {
    const read = el(
      "button",
      { type: "button", class: "pbtn pri" },
      icon("check"),
      " I've read this",
    );
    read.addEventListener("click", async () => {
      read.disabled = true;
      const r = await api<{ beforeYouStart: BeforeYouStart }>("POST", `${base}/routine/read`);
      read.disabled = false;
      if (!r.ok) return toast("That wasn't saved", r.body.error);
      b = r.body.beforeYouStart;
      draw();
      toast("Marked read", "In the visit's history");
    });
    const changed = el(
      "button",
      { type: "button", class: "pbtn" },
      icon("alert"),
      " Something changed",
    );
    changed.addEventListener("click", () =>
      somethingChanged(shell, b, (next) => {
        b = next;
        draw();
      }),
    );
    card.replaceChildren(
      el(
        "div",
        { class: "prow2" },
        el("b", {}, icon("home"), ` Before you start · ${b.first}`),
        b.read ? chip("Read", "ok") : chip("First visit", "info"),
      ),
      el("p", { class: "small muted", style: "margin:2px 0 6px" }, b.words),
      ...b.highlights.map((x) =>
        el(
          "div",
          { class: "byshl" },
          x.photoFileId
            ? photoImg(`${base}/photos/${x.photoFileId}`, `Photo: ${x.body}`, "rtile")
            : null,
          el("span", { class: "small" }, x.body),
          x.marked ? chip("Changed? Check with the nurse", "warn") : null,
        ),
      ),
      ...(b.talk.length
        ? [
            el(
              "div",
              { class: "small", style: "margin:8px 0 4px" },
              el("b", {}, "Conversation starters"),
            ),
            el("div", { class: "linkrow" }, ...b.talk.map((t) => chip(t, "info"))),
          ]
        : []),
      el(
        "div",
        { class: "actions", style: "margin-top:8px" },
        ...(b.read ? [] : [read]),
        ...(b.items.length ? [changed] : []),
      ),
    );
  };
  draw();
  return card;
}

/** Something changed: the caregiver picks the item that's different, and it goes to the nurse. */
function somethingChanged(
  shell: HTMLElement,
  b: BeforeYouStart,
  done: (next: BeforeYouStart) => void,
) {
  const close = () => {
    scrim.remove();
    sheet.remove();
  };
  const scrim = el("div", { class: "psheet-scrim" });
  scrim.addEventListener("click", close);
  const pick = el(
    "div",
    { class: "rpick", role: "radiogroup", "aria-label": "What changed?" },
    ...b.items.map((x) => {
      const input = el("input", {
        type: "radio",
        name: "rchanged",
        value: x.id,
        disabled: x.marked,
      });
      return el(
        "label",
        { class: "ptask" },
        input,
        el("span", {}, x.body, el("br"), el("span", { class: "small muted" }, x.section)),
        x.marked ? chip("Sent to the nurse", "plain") : null,
      );
    }),
  );
  const send = el(
    "button",
    { type: "button", class: "pbtn pri" },
    icon("check"),
    " Send to the nurse",
  );
  send.addEventListener("click", async () => {
    const id = (pick.querySelector("input:checked") as HTMLInputElement | null)?.value;
    if (!id) return toast("Pick what changed");
    send.disabled = true;
    const r = await api<{ beforeYouStart: BeforeYouStart | null }>(
      "POST",
      `/api/field/visits/${b.visitId}/routine/items/${id}/changed`,
    );
    send.disabled = false;
    if (!r.ok) return toast("That wasn't sent", r.body.error);
    close();
    if (r.body.beforeYouStart) done(r.body.beforeYouStart);
    toast("Sent to the nurse", "Marked for review in Home and routine");
  });
  const cancel = el("button", { type: "button", class: "pbtn" }, "Cancel");
  cancel.addEventListener("click", close);
  const sheet = el(
    "div",
    { class: "psheet tall", role: "dialog", "aria-label": "What changed?" },
    el("div", { class: "psh-grab" }),
    el("h2", { class: "ph2" }, "What changed?"),
    pick,
    el("div", { class: "actions" }, send, cancel),
  );
  shell.append(scrim, sheet);
  (sheet.querySelector("input:not([disabled])") as HTMLElement | null)?.focus();
}

/**
 * Running late? (brief 1E-18, mockup v76): once the start plus the company's minutes has passed
 * with no check-in and the phone away from the home, a text to the patient with an ETA, from the
 * office number. Fills `slot` when it shows.
 */
async function runningLateCard(slot: HTMLElement, c: Card) {
  // Brief 1E-19: it shows with no location for the phone too.
  const point = await locate();
  const base = `/api/field/visits/${c.id}`;
  const r = await api<{
    shows: boolean;
    sent: { words: string } | null;
    words?: string;
    etas?: number[];
    hint?: string;
    /** Texts OK off on the patient: the office's number to call instead. */
    callOffice?: string | null;
  }>("GET", `${base}/running-late${point ? `?lat=${point.lat}&lng=${point.lng}` : ""}`);
  if (!r.ok || (!r.body.shows && !r.body.sent)) return;
  const draw = (sent: string | null) => {
    slot.replaceChildren(
      el(
        "section",
        { class: "pcard remind", "aria-label": "Running late?" },
        el("b", {}, icon("clock"), " Running late?"),
        r.body.words ? el("p", { class: "small", style: "margin:4px 0" }, r.body.words) : null,
        ...(sent
          ? [chip(sent, "ok")]
          : [
              el(
                "div",
                { class: "actions" },
                ...(r.body.etas ?? []).map((m) => {
                  const b = el("button", { type: "button", class: "chipbtn" }, `${m} min`);
                  b.addEventListener("click", async () => {
                    b.disabled = true;
                    const where = (await locate()) ?? point;
                    const x = await api<{ sent: { words: string } }>("POST", `${base}/late-text`, {
                      minutes: m,
                      lat: where?.lat ?? null,
                      lng: where?.lng ?? null,
                    });
                    b.disabled = false;
                    if (!x.ok) return toast("The text didn't go", x.body.error);
                    draw(x.body.sent.words);
                    toast("Text sent", "The office sees it on the visit");
                  });
                  return b;
                }),
              ),
              r.body.callOffice
                ? el(
                    "a",
                    { class: "pbtn", href: `tel:${r.body.callOffice}` },
                    icon("device-mobile"),
                    " Call the office",
                  )
                : null,
              el("span", { class: "small muted" }, r.body.hint ?? ""),
            ]),
      ),
    );
  };
  draw(r.body.sent?.words ?? null);
}

/**
 * The orientation checklist (brief 1E-18, mockup v76): each person checks every item on their own
 * phone, with a comment if they want. An item one of them didn't check goes to the manager.
 */
function orientationCard(first: OrientationCheck): HTMLElement {
  let o = first;
  const card = el("section", { class: "pcard", "aria-label": `Orientation · ${o.patientFirst}` });
  // Saves go one at a time, each with what's wanted for its item at the moment it's sent (a
  // comment typed, then a tick, is two saves): an earlier answer never undoes a later tick.
  let queue: Promise<void> = Promise.resolve();
  let waiting = 0;
  const wanted = new Map<string, { checked: boolean; comment: string }>();
  const draw = () => {
    const mine = (i: OrientationCheck["items"][number]) =>
      o.side === "orienting" ? i.orienting : i.new;
    const myComment = (i: OrientationCheck["items"][number]) =>
      o.side === "orienting" ? i.orientingComment : i.newComment;
    const save = (i: OrientationCheck["items"][number], checked: boolean, comment: string) => {
      wanted.set(i.id, { checked, comment });
      waiting++;
      queue = queue.then(async () => {
        const want = wanted.get(i.id) ?? { checked, comment };
        const r = await api<{ orientation: OrientationCheck }>(
          "PUT",
          `/api/field/orientations/${o.id}/items/${i.id}`,
          want,
        );
        waiting--;
        if (!r.ok) {
          toast("That wasn't saved", r.body.error);
          if (!waiting) draw();
          return;
        }
        o = r.body.orientation;
        if (waiting) return;
        wanted.clear();
        draw();
        if (o.status === "oriented")
          toast("Both agree", `${o.who} is oriented to ${o.patientFirst}`);
      });
    };
    card.replaceChildren(
      el(
        "div",
        { class: "prow2" },
        el("b", {}, icon("people"), ` Orientation · ${o.patientFirst}`),
        chip(o.chip.words, o.chip.level),
      ),
      el(
        "p",
        { class: "small muted", style: "margin:2px 0 6px" },
        o.side === "orienting"
          ? `Showing ${o.who} · ${o.date}. Check each item once it's shown.`
          : `With ${o.by} · ${o.date}. Check each item once it's shown.`,
      ),
      ...o.items.map((i, n) => {
        const box = el("input", {
          type: "checkbox",
          id: `or-${o.id}-${n}`,
          checked: mine(i),
          disabled: !o.canCheck,
        });
        const note = el("input", {
          type: "text",
          id: `orc-${o.id}-${n}`,
          maxlength: "300",
          value: myComment(i),
          placeholder: "Comment (optional)",
          disabled: !o.canCheck,
          "aria-label": `Comment: ${i.body}`,
        });
        box.addEventListener("change", () => void save(i, box.checked, note.value));
        note.addEventListener("change", () => void save(i, box.checked, note.value));
        return el(
          "div",
          { class: "oritem" },
          el("label", { class: "ptask", for: `or-${o.id}-${n}` }, box, el("span", {}, i.body)),
          i.agreed ? chip("Both checked", "ok") : null,
          note,
        );
      }),
      ...(o.items.length
        ? []
        : [el("p", { class: "small muted" }, "Nothing in the guide to check.")]),
    );
  };
  draw();
  return card;
}

/**
 * An appointment during one of this caregiver's visits (brief 1E-19, mockup v79): "Tomorrow:
 * Robert has an appointment", the two choices, and once answered, the answer.
 */
function askCard(a: Ask): HTMLElement {
  const card = el("section", { class: "pcard remind", "aria-label": a.title });
  const draw = (answer: string | null) =>
    card.replaceChildren(
      el("b", {}, icon("calendar"), ` ${a.title}`),
      el("p", { class: "small", style: "margin:4px 0" }, a.words),
      answer
        ? chip(answer, "ok")
        : el(
            "div",
            { class: "actions" },
            ...a.choices.map((c, i) => {
              const b = el(
                "button",
                { type: "button", class: `tbtn${i === 0 ? " pri" : ""}` },
                c.label,
              );
              b.addEventListener("click", async () => {
                b.disabled = true;
                const r = await api<{ ask: Ask | null }>("POST", `/api/field/asks/${a.id}`, {
                  answer: c.value,
                });
                b.disabled = false;
                if (!r.ok) return toast("That wasn't sent", r.body.error);
                draw(r.body.ask?.answer ?? c.label);
                toast("Sent to the office");
              });
              return b;
            }),
          ),
    );
  draw(a.answer);
  return card;
}

/**
 * Call before you leave on the next visit (brief 1E-19, mockup v79): Call rings this phone, then
 * the patient, through the office number; No answer is noted. Either is recorded on the visit.
 */
function callBeforeCard(c: NonNullable<Home["callBefore"]>): HTMLElement {
  const card = el("section", { class: "pcard", "aria-label": "Call before you leave" });
  const draw = (done: string | null) => {
    const pick = (answer: "called" | "no_answer", label: string) => {
      const b = el("button", { type: "button", class: "chipbtn" }, label);
      b.addEventListener("click", async () => {
        b.disabled = true;
        const r = await api<{ callBefore: { done: string } }>(
          "POST",
          `/api/field/visits/${c.visitId}/call-before`,
          { answer },
        );
        b.disabled = false;
        if (!r.ok) return toast("That didn't go", r.body.error);
        draw(r.body.callBefore.done);
        toast(answer === "called" ? "Calling through the office number" : "Noted on the visit");
      });
      return b;
    };
    card.replaceChildren(
      el(
        "div",
        { class: "prow2" },
        el(
          "span",
          {},
          el("b", {}, icon("device-mobile"), " Call before you leave"),
          el("br"),
          el("span", { class: "small muted" }, "Let the patient know when you'll be there"),
        ),
        done
          ? chip(done, "ok")
          : el(
              "span",
              { class: "actions" },
              pick("called", "Call"),
              pick("no_answer", "No answer"),
            ),
      ),
    );
  };
  draw(c.done);
  return card;
}

function careTab(shell: HTMLElement, body: HTMLElement, h: Home, go: (p: string) => void) {
  const c = h.now ?? h.next;
  // Running late? for the next visit, once its start has passed with no check-in (brief 1E-18).
  const lateSlot = el("div");
  if (c && !h.now && !c.checkedIn && Date.now() > new Date(c.startsAt).getTime())
    void runningLateCard(lateSlot, c);
  body.append(
    lateSlot,
    ...(h.asks ?? []).map(askCard),
    ...(h.orientations ?? []).map(orientationCard),
    ...(h.beforeYouStart && c && h.beforeYouStart.visitId === c.id
      ? [beforeYouStartCard(shell, h.beforeYouStart)]
      : []),
    c
      ? el(
          "section",
          { class: "pcard hero", "aria-label": h.now ? "Visit now" : "Next visit" },
          el("div", { class: "eyebrow" }, h.now ? "Visit now" : "Next visit"),
          el(
            "div",
            { class: "nv-when" },
            c.day === h.today
              ? (c.hours.split(" – ")[0] ?? c.hours)
              : `${c.weekday} ${c.date.slice(0, 5)}`,
          ),
          withWhom(c.id, c.patientFirst, c.patientPhoto),
          el(
            "p",
            { class: "small muted", style: "margin:2px 0 8px" },
            `${c.service} · ${when(c, h.today)}`,
          ),
          pbtn(
            [
              icon(h.now ? "checklist" : "sign-in"),
              h.now ? " Continue visit" : " Open visit · check in",
            ],
            () => go(`/field/visit/${c.id}`),
            { class: "pbtn pri big" },
          ),
        )
      : el(
          "section",
          { class: "pcard" },
          el(
            "p",
            { class: "muted", style: "margin:0" },
            "No visits coming up. New ones show here once the office publishes them.",
          ),
        ),
    ...(c && h.callBefore && h.callBefore.visitId === c.id ? [callBeforeCard(h.callBefore)] : []),
    el("h2", { class: "ph2" }, "The rest of today"),
    ...(h.rest.length
      ? h.rest.map((r) => {
          const b = el(
            "button",
            { type: "button", class: "pcard" },
            el(
              "div",
              { class: "prow2", style: "width:100%" },
              el(
                "span",
                {},
                el("b", {}, r.hours),
                el("br"),
                el("span", { class: "small muted" }, r.patientFirst),
              ),
              stateChip(r),
            ),
          );
          b.addEventListener("click", () => go(`/field/visit/${r.id}`));
          return b;
        })
      : [el("p", { class: "muted" }, "Nothing else today.")]),
    el("h2", { class: "ph2" }, "This week"),
    el(
      "div",
      { class: "pgrid" },
      ...[
        ["Visits", String(h.week.visits)],
        ["Done", String(h.week.done)],
        ["Hours", h.week.hours.toFixed(2)],
      ].map(([l, v]) =>
        el(
          "div",
          { class: "pstat2" },
          el("span", { class: "muted small" }, l as string),
          el("b", {}, v as string),
        ),
      ),
    ),
    ...(h.toAnswer
      ? [
          pbtn([icon("calendar"), ` ${h.toAnswer} to answer on the schedule`], () =>
            go("/field/schedule"),
          ),
        ]
      : []),
  );
}

// ---- Visit ---------------------------------------------------------------------------------------------------

async function visitTab(
  body: HTMLElement,
  h: Home,
  me: Me,
  go: (p: string) => void,
  id: string | null,
) {
  if (!id) {
    body.append(
      el(
        "div",
        { class: "pempty" },
        icon("checklist", "big"),
        el("h2", {}, "No visit open"),
        el("p", { class: "muted" }, "The next visit opens here. Check the schedule."),
        pbtn([icon("calendar"), " Schedule"], () => go("/field/schedule")),
      ),
    );
    return;
  }
  const first = await api<VisitView>("GET", `/api/field/visits/${id}`);
  if (!first.ok) {
    body.append(
      el("p", { class: "note", role: "alert" }, "This visit isn't on this caregiver's schedule."),
    );
    return;
  }
  let v = first.body;
  const base = `/api/field/visits/${id}`;
  const redraw = (next: VisitView) => {
    v = next;
    draw();
  };
  const call = async (method: string, path: string, data: unknown, ok?: string, sub?: string) => {
    const res = await api<VisitView>(method, `${base}${path}`, data);
    if (!res.ok) {
      toast("That wasn't saved", res.body.error);
      return false;
    }
    redraw(res.body);
    if (ok) toast(ok, sub);
    return true;
  };

  // ---- 1 · Check in, and check out (never refused)
  async function doPunch(kind: "in" | "out", selfie: File | null) {
    const phoneAt = new Date().toISOString();
    let selfieFileId: string | null = null;
    if (selfie) {
      const up = await uploadFile(selfie, { kind: "visit", id: v.visit.id }).catch(() => null);
      if (up?.ok) selfieFileId = up.file.id;
    }
    const point = await locate();
    const data = {
      kind,
      phoneAt,
      lat: point?.lat ?? null,
      lng: point?.lng ?? null,
      selfieFileId,
      unlocked: false,
      sentLate: false,
    };
    try {
      if (!navigator.onLine) throw new Error("offline");
      const res = await api<VisitView>("POST", `${base}/punch`, data);
      if (!res.ok) return toast("That wasn't saved", res.body.error);
      redraw(res.body);
      const p = kind === "in" ? res.body.checkIn.in : res.body.checkIn.out;
      // Brief 1E-30: outside the window the check-in waits for the office's OK.
      const outside = p?.flags.find((f) => f.key === "punch_window");
      toast(
        kind === "in" ? `Checked in ${p?.at ?? ""}` : `Checked out ${p?.at ?? ""}`,
        outside
          ? `Sent to the office. ${outside.words}`
          : p?.flags.length
            ? "Sent to the office with a note of what's unusual"
            : "Sent to the office",
      );
    } catch {
      // No signal: kept on the phone and sent once back online, with the phone's own time.
      keep(me, [...queued(me), { path: `${base}/punch`, body: { ...data, sentLate: true } }]);
      toast(
        kind === "in" ? "Check-in kept on this phone" : "Check-out kept on this phone",
        "No signal. It's sent with this time once back online.",
      );
    }
  }

  let noteDraft: string | null = null;
  let noteDictated = false;
  const slot = el("div", { class: "pcol" });
  function draw() {
    const inP = v.checkIn.in;
    const outP = v.checkIn.out;
    const started = !!inP;
    const done = !!outP;
    const dim = started && !done ? "" : started ? "" : " dim";
    // Sent back by the office (brief 1D-4): what to fix, editable again, and Send it again.
    const back = v.sentBack;
    const sentBack = back
      ? (() => {
          const again = pbtn(
            [icon("paper-airplane"), " Send it to the office again"],
            () =>
              void call(
                "POST",
                "/resubmit",
                {},
                "Sent to the office again",
                "It's back with the office to check",
              ),
            { class: "pbtn pri big" },
          );
          return el(
            "section",
            { class: "pcard", role: "note", "aria-label": "Sent back by the office" },
            el("b", {}, icon("comment"), " Sent back by the office"),
            el("p", { style: "margin:4px 0" }, back.note),
            el(
              "p",
              { class: "small muted", style: "margin:0 0 8px" },
              `${back.at}. Fix it below, then send it again.`,
            ),
            again,
          );
        })()
      : null;

    // Check in
    const selfie = el("input", {
      type: "file",
      accept: "image/*",
      capture: "user",
      hidden: true,
      id: "fv-selfie",
      "aria-label": "Selfie for check-in",
    });
    selfie.addEventListener("change", () => void doPunch("in", selfie.files?.[0] ?? null));
    // No camera, or the camera closed: the check-in still goes (flagged: no selfie).
    selfie.addEventListener("cancel", () => void doPunch("in", null));
    const checkIn = el(
      "section",
      { class: "pcard", "aria-label": "Check in" },
      el("div", { class: "prow2" }, el("b", {}, "1 · Check in"), inP ? chip(inP.at, "ok") : ""),
      ...(inP
        ? [
            el(
              "p",
              { class: "small muted", style: "margin:4px 0 0" },
              [
                inP.selfie ? "Selfie taken" : "No selfie",
                inP.distance ? `GPS ${inP.distance} from the home` : "No distance",
                inP.sentLate ? "sent late" : "",
              ]
                .filter(Boolean)
                .join(" · "),
            ),
            ...flagLines(inP.flags),
            ...(outP ? [] : [onTheClock()]),
          ]
        : [
            el(
              "p",
              { class: "small muted" },
              `Takes a selfie and the location. ${v.checkIn.open ? "Check-in is open." : `Check-in opens at ${v.checkIn.opensAt}. Earlier than that is flagged.`}`,
            ),
            el(
              "label",
              { class: "pbtn pri big", for: "fv-selfie", role: "button", tabindex: "0" },
              icon("device-camera"),
              " Check in",
            ),
            selfie,
          ]),
    );

    // Due this visit, the other care tasks, Also done and the note
    const marked = v.tasks.filter((t) => t.result).length;
    const tasks = el(
      "section",
      { class: `pcard${dim}`, "aria-label": "Due this visit" },
      el(
        "div",
        { class: "prow2" },
        el("b", {}, "2 · Due this visit"),
        el("span", { class: "small muted" }, `${marked} of ${v.tasks.length}`),
      ),
      el("p", { class: "small muted", style: "margin:4px 0 6px" }, v.dueWords),
      ...(v.tasks.length
        ? v.tasks.map((t) => taskRow(t))
        : [el("p", { class: "small muted" }, "No care tasks due this visit.")]),
      otherTasks(),
      alsoDoneField(),
      noteField(),
    );

    // Sign-off, mileage, check out
    slot.replaceChildren(
      el(
        "section",
        { class: "pcard" },
        el("div", { class: "eyebrow" }, `Visit · ${when(v.visit, h.today)}`),
        withWhom(v.visit.id, v.patient?.first ?? v.visit.patientFirst, v.patient?.photoFileId),
        el(
          "p",
          { class: "small muted", style: "margin:2px 0 0" },
          v.patient?.address || v.visit.service,
        ),
      ),
      ...(sentBack ? [sentBack] : []),
      checkIn,
      ...(h.orientations ?? [])
        .filter((o) => o.visitId === v.visit.id)
        .map((o) => orientationCard(o)),
      tasks,
      signoffCard(started || !!back),
      mileageCard(started),
      ...(v.beforeYouLeave && !done ? [beforeYouLeaveCard(v.beforeYouLeave)] : []),
      ...(done
        ? [
            el(
              "div",
              { class: "pcard", role: "status" },
              chip(`Checked out ${outP.at}`, "ok"),
              " ",
              el("span", { class: "small" }, "Sent to the office."),
              ...flagLines(outP.flags),
            ),
          ]
        : [
            pbtn([icon("sign-out"), " Check out and submit"], () => void doPunch("out", null), {
              class: "pbtn pri big",
            }),
            el(
              "p",
              { class: "small muted", style: "text-align:center", id: "fv-outwords" },
              started
                ? v.checkoutWords
                : "Check in first. Checking out without it still works; the office is told.",
            ),
          ]),
    );
  }

  // Before you leave (brief 1E-18, mockup v78): what's coming for the patient, before check-out.
  function beforeYouLeaveCard(b: NonNullable<VisitView["beforeYouLeave"]>) {
    const told = pbtn(
      [icon("check"), " Told the patient"],
      () => void call("POST", "/told", {}, "Noted on the visit"),
    );
    return el(
      "section",
      { class: "pcard", "aria-label": "Before you leave" },
      el(
        "div",
        { class: "prow2" },
        el("b", {}, icon("calendar"), " Before you leave"),
        b.told ? chip("Told", "ok") : "",
      ),
      el(
        "p",
        { class: "small muted", style: "margin:2px 0 6px" },
        "What's coming. Let the patient know.",
      ),
      ...(b.items.length
        ? b.items.map((n) =>
            el(
              "div",
              { class: "prow2", style: "margin:4px 0" },
              el("span", { class: "small" }, el("b", {}, n.when), el("br"), n.what),
              icon(n.icon),
            ),
          )
        : [el("p", { class: "small muted" }, "Nothing booked for tomorrow.")]),
      b.told ? "" : told,
    );
  }

  function flagLines(flags: { words: string }[]) {
    return flags.map((f) =>
      el("p", { class: "small warnline", style: "margin:4px 0 0" }, icon("alert"), ` ${f.words}`),
    );
  }

  function taskRow(t: VisitView["tasks"][number]) {
    const started = (!!v.checkIn.in && !v.checkIn.out) || !!v.sentBack;
    const why = el("div", { class: "field", hidden: true });
    const seg = el(
      "div",
      { class: "seg4", role: "group", "aria-label": t.task },
      ...v.results.map((r) => {
        const b = el(
          "button",
          {
            type: "button",
            "aria-pressed": t.result === r.value ? "true" : "false",
            disabled: !started,
          },
          r.label,
        );
        b.addEventListener("click", () => {
          if (r.value === "done") return void call("PUT", `/tasks/${t.id}`, { result: "done" });
          // Not done and Refused ask why.
          const input = el("input", {
            type: "text",
            id: `why-${t.id}`,
            value: t.note ?? "",
            maxlength: "500",
          });
          const save = el("button", { type: "button", class: "btn sm pri" }, "Save");
          save.addEventListener(
            "click",
            () =>
              void call(
                "PUT",
                `/tasks/${t.id}`,
                { result: r.value, note: input.value },
                `${r.label}: ${t.task}`,
              ),
          );
          input.placeholder = "Why? (needed)";
          why.replaceChildren(
            el("label", { for: `why-${t.id}` }, `Why is it ${r.label.toLowerCase()}?`),
            input,
            save,
          );
          why.hidden = false;
          input.focus();
        });
        return b;
      }),
    );
    return el(
      "div",
      { class: "ftask" },
      el("span", {}, t.task, el("span", { class: "ftag" }, t.label)),
      seg,
      t.note && t.result !== "done" ? el("span", { class: "small muted" }, t.note) : "",
      why,
    );
  }

  // On the clock · h:mm, counting from check-in, and the scheduled hours.
  let clockTimer: ReturnType<typeof setInterval> | undefined;
  function onTheClock() {
    const since = v.checkIn.since ? new Date(v.checkIn.since).getTime() : Date.now();
    const words = el("b", {}, "");
    const tick = () => {
      const m = Math.max(0, Math.floor((Date.now() - since) / 60_000));
      words.textContent = `On the clock · ${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
    };
    tick();
    clearInterval(clockTimer);
    clockTimer = setInterval(() => {
      if (!words.isConnected) return clearInterval(clockTimer);
      tick();
    }, 30_000);
    return el(
      "div",
      { class: "prow2", style: "margin-top:6px" },
      el("span", { class: "small" }, words),
      el("span", { class: "small muted" }, `Scheduled ${v.checkIn.scheduled}`),
    );
  }

  // Other care tasks: the plan's tasks not due this visit, with when each is next, and Done today.
  function otherTasks() {
    if (!v.otherTasks.length) return el("span");
    const started = (!!v.checkIn.in && !v.checkIn.out) || !!v.sentBack;
    return el(
      "div",
      { class: "tsother", role: "group", "aria-label": "Other care tasks" },
      el("b", { class: "small" }, "Other care tasks"),
      ...v.otherTasks.map((t) => {
        const b = el(
          "button",
          {
            type: "button",
            class: "btn sm",
            disabled: !started,
            "aria-label": `Done today: ${t.task}`,
          },
          "Done today",
        );
        b.addEventListener(
          "click",
          () => void call("PUT", `/tasks/${t.id}`, { result: "done" }, `Done today: ${t.task}`),
        );
        return el(
          "div",
          { class: "prow2" },
          el("span", {}, t.task, el("span", { class: "ftag" }, t.next)),
          b,
        );
      }),
    );
  }

  // Also done this visit: anything extra, in a few words (autosaved).
  let alsoDraft: string | null = null;
  function alsoDoneField() {
    const box = el("input", {
      type: "text",
      id: "fv-extra",
      maxlength: "300",
      placeholder: "e.g. Swept the kitchen floor",
      value: alsoDraft ?? v.alsoDone,
      disabled: !v.checkIn.in && !v.sentBack,
    });
    const status = el("span", { class: "small muted", role: "status" });
    let timer: ReturnType<typeof setTimeout> | undefined;
    box.addEventListener("input", () => {
      alsoDraft = box.value;
      status.textContent = "Saving…";
      clearTimeout(timer);
      timer = setTimeout(async () => {
        const sent = box.value;
        const res = await api<VisitView>("PUT", `${base}/also-done`, { alsoDone: sent });
        if (res.ok) {
          v = res.body;
          if (alsoDraft === sent) alsoDraft = null;
          status.textContent = "Saved";
        } else status.textContent = res.body.error ?? "Not saved";
      }, 700);
    });
    return el(
      "div",
      { class: "field" },
      el("label", { for: "fv-extra" }, "Also done this visit"),
      box,
      status,
    );
  }

  function noteField() {
    // What's typed survives a redraw (a task marked) until it's saved.
    const box = el(
      "textarea",
      { id: "fv-note", disabled: !v.checkIn.in && !v.sentBack },
      noteDraft ?? v.note.body,
    );
    if (noteDictated) box.dataset.dictated = "1";
    let timer: ReturnType<typeof setTimeout> | undefined;
    const save = async () => {
      const sent = box.value;
      const res = await api<VisitView>("PUT", `${base}/note`, {
        body: sent,
        dictated: box.dataset.dictated === "1",
      });
      if (res.ok) {
        v = res.body;
        if (noteDraft === sent) noteDraft = null;
        status.textContent = "Saved";
        // What check-out would flag, now that the note is there.
        const words = document.getElementById("fv-outwords");
        if (words && v.checkIn.in) words.textContent = v.checkoutWords;
      } else status.textContent = res.body.error ?? "Not saved";
    };
    const status = el("span", { class: "small muted", role: "status" });
    // After check-out a change is a late entry: it asks why and saves with Save (brief 1E-18).
    const late = !!v.noteNeedsReason;
    const why = el("input", {
      type: "text",
      id: "fv-late",
      maxlength: "300",
      placeholder: "Why is it changed now?",
    });
    const lateSave = el("button", { type: "button", class: "btn sm pri" }, "Save");
    const lateBox = el(
      "div",
      { class: "field", style: "display:none" },
      el("label", { for: "fv-late" }, "Why is the note changed after check-out?"),
      why,
      lateSave,
    );
    lateSave.addEventListener("click", async () => {
      if (!why.value.trim()) return toast("Say why it's changed now");
      lateSave.disabled = true;
      const res = await api<VisitView>("PUT", `${base}/note`, {
        body: box.value,
        lateReason: why.value,
      });
      lateSave.disabled = false;
      if (!res.ok) return toast("That wasn't saved", res.body.error);
      noteDraft = null;
      redraw(res.body);
      toast("Saved as a late entry", why.value.trim());
    });
    box.addEventListener("input", () => {
      noteDraft = box.value;
      if (box.dataset.dictated === "1") noteDictated = true;
      if (late) {
        lateBox.style.display = box.value === v.note.body ? "none" : "";
        return;
      }
      status.textContent = "Saving…";
      clearTimeout(timer);
      timer = setTimeout(() => void save(), 700);
    });
    return el(
      "div",
      { class: "field" },
      el("label", { for: "fv-note" }, "Note for the office"),
      box,
      status,
      v.noteLate ? chip(v.noteLate.words, "warn") : null,
      lateBox,
    );
  }

  function signoffCard(started: boolean) {
    const s = v.signoff;
    const dim = started ? "" : " dim";
    if (s.done)
      return el(
        "section",
        { class: "pcard", "aria-label": "Sign off" },
        el("b", {}, "3 · Sign off"),
        el(
          "p",
          { class: "small", style: "margin:4px 0 0" },
          chip(s.done.signer ? `Signed by ${s.done.signer}` : s.done.label, "ok"),
          ` ${s.done.at}`,
        ),
      );
    if (s.rule === "sig") return signatureCard(started);
    const box = el("input", { type: "checkbox", disabled: !started, id: "fv-attest" });
    box.addEventListener("change", () => {
      if (box.checked)
        void call("POST", "/signoff", { kind: s.choices[0]?.value, attest: true }, "Signed off");
    });
    return el(
      "section",
      { class: `pcard${dim}`, "aria-label": "Sign off" },
      el("b", {}, "3 · Sign off"),
      s.rule === "onfile"
        ? el(
            "p",
            { class: "small muted", style: "margin:4px 0" },
            "Patient signature on file (signed at admission).",
          )
        : "",
      el(
        "label",
        { class: "attest", for: "fv-attest" },
        box,
        " I provided the care marked above during this visit.",
      ),
    );
  }

  function signatureCard(started: boolean) {
    const s = v.signoff;
    const canvas = el("canvas", {
      class: "sigcv",
      width: "600",
      height: "200",
      "aria-label": "Sign here",
      role: "img",
    });
    let drew = false;
    const g = canvas.getContext("2d");
    if (g) {
      g.lineWidth = 3;
      g.lineCap = "round";
      g.strokeStyle = "#191820";
    }
    let down = false;
    const at = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      return [
        ((e.clientX - r.left) / r.width) * canvas.width,
        ((e.clientY - r.top) / r.height) * canvas.height,
      ] as const;
    };
    canvas.addEventListener("pointerdown", (e) => {
      if (!started || !g) return;
      down = true;
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        // Some browsers refuse capture for an emulated pointer; drawing still works without it.
      }
      const [x, y] = at(e);
      g.beginPath();
      g.moveTo(x, y);
    });
    canvas.addEventListener("pointermove", (e) => {
      if (!down || !g) return;
      const [x, y] = at(e);
      g.lineTo(x, y);
      g.stroke();
      drew = true;
    });
    canvas.addEventListener("pointerup", () => (down = false));
    const who = el(
      "div",
      { class: "seg4", role: "group", "aria-label": "Who signs" },
      ...s.choices.map((c, i) => {
        const b = el(
          "button",
          {
            type: "button",
            "aria-pressed": i === 0 ? "true" : "false",
            "data-kind": c.value,
            disabled: !started,
          },
          c.label,
        );
        b.addEventListener("click", () => {
          for (const x of who.querySelectorAll("button"))
            x.setAttribute("aria-pressed", x === b ? "true" : "false");
        });
        return b;
      }),
    );
    const name = el("input", {
      type: "text",
      id: "fv-signer",
      maxlength: "120",
      disabled: !started,
    });
    const clear = el(
      "button",
      {
        type: "button",
        class: "btn ib sm",
        "aria-label": "Clear the signature",
        "data-tip": "Clear the signature",
        disabled: !started,
      },
      icon("x"),
    );
    clear.addEventListener("click", () => {
      g?.clearRect(0, 0, canvas.width, canvas.height);
      drew = false;
    });
    const save = el(
      "button",
      { type: "button", class: "pbtn pri", disabled: !started },
      icon("check"),
      " Save signature",
    );
    save.addEventListener("click", async () => {
      if (!drew) return toast("Sign in the box first");
      if (!name.value.trim()) return toast("Who's signing?", "Type their name");
      save.disabled = true;
      const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/png"));
      if (!blob) {
        save.disabled = false;
        return toast("The signature wasn't kept", "Try again");
      }
      const up = await uploadFile(new File([blob], "signature.png", { type: "image/png" }), {
        kind: "visit",
        id: v.visit.id,
      });
      if (!up.ok) {
        save.disabled = false;
        return toast("The signature wasn't kept", up.error);
      }
      const kind = (who.querySelector('[aria-pressed="true"]') as HTMLElement | null)?.dataset.kind;
      const okd = await call(
        "POST",
        "/signoff",
        { kind, signerName: name.value, fileId: up.file.id },
        "Signed",
        `By ${name.value.trim()}`,
      );
      if (!okd) save.disabled = false;
    });
    return el(
      "section",
      { class: `pcard${started ? "" : " dim"}`, "aria-label": "Signature" },
      el("b", {}, "3 · Signature"),
      el(
        "p",
        { class: "small muted", style: "margin:4px 0" },
        "This client asks for the patient's or family's signature.",
      ),
      who,
      el("div", { class: "field" }, el("label", { for: "fv-signer" }, "Signer's name"), name),
      el("div", { class: "sigpad" }, canvas, el("span", { class: "actions" }, clear)),
      save,
    );
  }

  function mileageCard(started: boolean) {
    const m = v.mileage;
    const miles = el("input", {
      type: "number",
      id: "fv-mi",
      min: "0",
      step: "0.1",
      inputmode: "decimal",
      value: m ? String(m.miles) : "",
      disabled: !started,
    });
    const purpose = el("input", {
      type: "text",
      id: "fv-mip",
      maxlength: "200",
      value: m?.purpose ?? "",
      disabled: !started,
    });
    const save = (receiptFileId?: string) =>
      call(
        "PUT",
        "/mileage",
        {
          miles: miles.value || 0,
          purpose: purpose.value,
          ...(receiptFileId ? { receiptFileId } : {}),
        },
        receiptFileId ? "Photo added" : "Mileage saved",
      );
    miles.addEventListener("change", () => void save());
    purpose.addEventListener("change", () => void save());
    const photo = el("input", {
      type: "file",
      accept: "image/*",
      capture: "environment",
      hidden: true,
      id: "fv-mir",
      "aria-label": "Receipt or odometer photo",
    });
    photo.addEventListener("change", async () => {
      const f = photo.files?.[0];
      if (!f) return;
      const up = await uploadFile(f, { kind: "visit", id: v.visit.id });
      if (!up.ok) return toast("The photo wasn't kept", up.error);
      await save(up.file.id);
    });
    return el(
      "section",
      { class: `pcard${started ? "" : " dim"}`, "aria-label": "Mileage" },
      el(
        "div",
        { class: "prow2" },
        el("b", {}, "Mileage"),
        el("span", { class: "small muted" }, "Paid per mile"),
      ),
      el(
        "div",
        { class: "field" },
        el("label", { for: "fv-mi" }, "Miles driven for this patient (errands, appointments)"),
        miles,
      ),
      el("div", { class: "field" }, el("label", { for: "fv-mip" }, "What for (optional)"), purpose),
      m?.receipt
        ? el("p", { class: "small" }, chip("Photo added", "ok"))
        : el(
            "label",
            { class: "ptask", for: "fv-mir" },
            icon("device-camera"),
            " Add a photo of the receipt or odometer",
          ),
      photo,
    );
  }

  draw();
  body.append(slot);
}

// ---- Schedule ------------------------------------------------------------------------------------------------

async function scheduleTab(
  body: HTMLElement,
  shell: HTMLElement,
  h: Home,
  go: (p: string) => void,
) {
  const res = await api<Sched>("GET", "/api/field/schedule");
  if (!res.ok) {
    body.append(
      el(
        "p",
        { class: "note", role: "alert" },
        res.body.error ?? "The schedule couldn't be loaded.",
      ),
    );
    return;
  }
  let s = res.body;
  let saying: string | null = null;
  const send = async (path: string, data: unknown, ok: string, sub: string) => {
    const r = await api<Sched>("POST", path, data);
    if (!r.ok) return toast("That wasn't sent", r.body.error);
    s = r.body;
    saying = null;
    draw();
    toast(ok, sub);
  };
  const line = (c: Card) => `${c.weekday} ${c.date.slice(0, 5)} · ${c.hours}`;
  const answerCard = (c: AnswerCard) => {
    const head = el(
      "div",
      { class: "prow2" },
      el(
        "span",
        {},
        el("b", {}, line(c)),
        el("br"),
        el("span", { class: "small muted" }, `${c.patientFirst} · ${c.service}`),
      ),
      c.confirm === "confirmed" ? chip("Confirmed", "ok") : "",
    );
    if (c.confirm !== "waiting")
      return el("div", { class: "pcard", role: "group", "aria-label": line(c) }, head);
    if (saying === c.id) {
      const why = el(
        "select",
        { id: `why-${c.id}` },
        el("option", { value: "" }, "Pick one"),
        ...s.reasons.map((r) => el("option", { value: r }, r)),
      );
      const note = el("input", { type: "text", id: `note-${c.id}`, maxlength: "250" });
      const go1 = pbtn(
        [icon("x-circle"), " Send"],
        () =>
          void send(
            `/api/field/visits/${c.id}/cannot-work`,
            { reason: why.value, note: note.value },
            "Sent to the office",
            "They'll find someone else",
          ),
      );
      const back = pbtn(["Back"], () => {
        saying = null;
        draw();
      });
      return el(
        "div",
        { class: "pcard", role: "group", "aria-label": line(c) },
        head,
        el(
          "div",
          { class: "field" },
          el("label", { for: `why-${c.id}` }, "Why it can't be worked"),
          why,
        ),
        el(
          "div",
          { class: "field" },
          el("label", { for: `note-${c.id}` }, "Anything else (optional)"),
          note,
        ),
        el("div", { class: "prow3" }, go1, back),
      );
    }
    // Brief 1E-34 (mockup v92): an optional note for the office goes with Confirm.
    const officeNote = el("input", {
      type: "text",
      id: `fkn-${c.id}`,
      maxlength: "300",
      placeholder: "Optional, e.g. need to leave at 12:45",
    });
    const confirm = pbtn(
      [icon("check"), " Confirm"],
      () =>
        void send(
          `/api/field/visits/${c.id}/confirm`,
          { note: officeNote.value },
          "Confirmed",
          officeNote.value.trim() ? "The office sees it with the note" : "The office sees it",
        ),
      { class: "pbtn pri" },
    );
    const cant = c.callOffice
      ? el(
          "button",
          { type: "button", class: "pbtn", disabled: true, "data-tip": c.callWords ?? "" },
          icon("x-circle"),
          " Can't work",
        )
      : pbtn([icon("x-circle"), " Can't work"], () => {
          saying = c.id;
          draw();
        });
    return el(
      "div",
      { class: "pcard", role: "group", "aria-label": line(c) },
      head,
      el(
        "div",
        { class: "field" },
        el("label", { for: `fkn-${c.id}` }, "Note for the office"),
        officeNote,
      ),
      el("div", { class: "prow3" }, confirm, cant),
      c.callOffice && c.callWords
        ? el("p", { class: "small muted", style: "margin:4px 0 0" }, c.callWords)
        : "",
    );
  };
  const openCard = (c: Sched["open"][number]) =>
    el(
      "div",
      { class: "pcard", role: "group", "aria-label": `Open visit ${line(c)}` },
      el(
        "div",
        { class: "prow2" },
        el(
          "span",
          {},
          el("b", {}, line(c)),
          el("br"),
          el(
            "span",
            { class: "small muted" },
            [
              c.patientFirst,
              c.service,
              c.miles === null
                ? ""
                : `${c.approx ? "about " : ""}${c.miles} mi${c.approx ? ", by ZIP" : ""}`,
            ]
              .filter(Boolean)
              .join(" · "),
          ),
        ),
        c.claimed
          ? chip("Requested", "info")
          : (() => {
              const b = el(
                "button",
                { type: "button", class: "pbtn", style: "width:auto;margin:0" },
                icon("plus"),
                " Claim",
              );
              b.addEventListener(
                "click",
                () =>
                  void send(
                    `/api/field/visits/${c.id}/claim`,
                    {},
                    "Claim sent",
                    "The office approves it",
                  ),
              );
              return b;
            })(),
      ),
      c.claimed
        ? el(
            "p",
            { class: "small muted", style: "margin:4px 0 0" },
            "The office approves claims and sends a text.",
          )
        : "",
    );
  const slot = el("div", { class: "pcol" });
  // Brief 1E-33: Earnings, read-only, by pay period.
  const earnSlot = el("div");
  void api<FieldEarnings>("GET", "/api/field/earnings").then((r) => {
    if (!r.ok) return;
    earnSlot.replaceChildren(
      el("h2", { class: "ph2" }, "Earnings"),
      r.body.rows.length
        ? el(
            "section",
            { class: "pcard", "aria-label": "Earnings" },
            ...r.body.rows.map((e) =>
              el(
                "div",
                { class: "prow2" },
                el(
                  "span",
                  {},
                  el("b", {}, e.period),
                  el("br"),
                  el(
                    "span",
                    { class: "small muted" },
                    `${e.visits} visit${e.visits === 1 ? "" : "s"} · ${e.hours} hrs`,
                  ),
                ),
                el(
                  "span",
                  { style: "text-align:right" },
                  el("b", {}, e.gross),
                  el("br"),
                  chip(e.status.text, e.status.level),
                ),
              ),
            ),
          )
        : el("p", { class: "muted" }, "No earnings yet."),
    );
  });
  const pastCard = (v: PastVisit) =>
    el(
      "section",
      { class: "pcard", "aria-label": `Past visit ${v.when}` },
      el(
        "div",
        { class: "prow2" },
        el(
          "span",
          {},
          el("b", {}, v.when),
          el("br"),
          el("span", { class: "small muted" }, `${v.patient} · ${v.hours}`),
        ),
        chip(v.status.text, v.status.level),
      ),
      v.tasks ? el("p", { class: "small", style: "margin:4px 0 0" }, v.tasks) : "",
      v.note ? el("p", { class: "small muted", style: "margin:4px 0 0" }, v.note) : "",
      ...v.flags.map((f) => el("p", { class: "small", style: "margin:4px 0 0" }, chip(f, "warn"))),
    );
  function draw() {
    slot.replaceChildren(
      ...(s.toAnswer.length
        ? [
            el("h2", { class: "ph2" }, `Please answer · ${s.toAnswer.length}`),
            ...s.toAnswer.map(answerCard),
          ]
        : []),
      el("h2", { class: "ph2" }, "Confirmed"),
      ...(s.confirmed.length
        ? s.confirmed.map(answerCard)
        : [el("p", { class: "muted" }, "Nothing confirmed yet.")]),
      ...(s.claiming
        ? [
            el("h2", { class: "ph2" }, "Open visits nearby"),
            ...(s.open.length
              ? s.open.map(openCard)
              : [el("p", { class: "muted" }, "No open visits nearby right now.")]),
          ]
        : []),
      el("h2", { class: "ph2" }, "Past"),
      ...(s.past.length
        ? s.past.map(pastCard)
        : [el("p", { class: "muted" }, "No visits worked in the last 30 days.")]),
      el(
        "p",
        { class: "small muted", style: "margin:4px 0 10px" },
        "Changes go through the office.",
      ),
      earnSlot,
      el(
        "div",
        { class: "prow3" },
        pbtn([icon("clock"), " My availability"], () => void availabilitySheet(shell, "week")),
        pbtn([icon("calendar"), " Time off"], () => void availabilitySheet(shell, "off")),
      ),
    );
  }
  draw();
  body.append(slot);
  void h;
  void go;
}

/** The My availability sheet (mockup fkAvail): the week, then Time off. */
async function availabilitySheet(shell: HTMLElement, focus: "week" | "off") {
  const res = await api<Avail>("GET", "/api/field/availability");
  if (!res.ok) return toast("Availability couldn't be loaded", res.body.error);
  let a = res.body;
  const close = () => {
    scrim.remove();
    sheet.remove();
  };
  const scrim = el("div", { class: "psheet-scrim" });
  scrim.addEventListener("click", close);
  const sheet = el("div", {
    class: "psheet tall",
    role: "dialog",
    "aria-label": "My availability",
  });
  const draw = () => {
    const grid = (a.asked ?? a.grid).map((d) => ({ ...d }));
    // The days in the order the company's week runs (brief 1D-5); the grid stays Sunday first.
    const rows = (a.order ?? [0, 1, 2, 3, 4, 5, 6]).map((i) => {
      const d = grid[i] as DaySetting;
      const day = a.days[i] ?? "";
      const start = el("input", {
        type: "time",
        value: d.start && d.kind !== "unavailable" ? d.start : "09:00",
        "aria-label": `${day} from`,
      });
      const end = el("input", {
        type: "time",
        value: d.end && d.kind !== "unavailable" ? d.end : "17:00",
        "aria-label": `${day} until`,
      });
      const times = el(
        "span",
        { class: "fatimes", hidden: !d.kind || d.kind === "unavailable" },
        start,
        " – ",
        end,
      );
      start.addEventListener("change", () => (d.start = start.value));
      end.addEventListener("change", () => (d.end = end.value));
      const seg = el(
        "div",
        { class: "seg", role: "radiogroup", "aria-label": day },
        ...a.kinds.map((k) => {
          const input = el("input", {
            type: "radio",
            name: `fa${i}`,
            value: k.value,
            checked: d.kind === k.value,
          });
          input.addEventListener("change", () => {
            d.kind = k.value as DaySetting["kind"];
            if (d.kind !== "unavailable") {
              d.start = start.value;
              d.end = end.value;
            }
            times.hidden = d.kind === "unavailable";
          });
          return el("label", {}, input, k.label);
        }),
      );
      return el("div", { class: "farow" }, el("b", {}, day), seg, times);
    });
    const save = el("button", { type: "button", class: "pbtn pri" }, icon("check"), " Save");
    save.addEventListener("click", async () => {
      save.disabled = true;
      const r = await api<Avail>("PUT", "/api/field/availability", {
        days: grid.map((d) => (d.kind ? d : { kind: null })),
      });
      save.disabled = false;
      if (!r.ok) return toast("That wasn't saved", r.body.error);
      a = r.body;
      draw();
      toast(
        r.body.waiting ? "Sent to the office first" : "Availability saved",
        r.body.waiting ? `It touches a visit within ${a.lockHours} hours` : "It repeats every week",
      );
    });
    const from = el("input", { type: "date", id: "fo1" });
    const until = el("input", { type: "date", id: "fo2" });
    const reason = el("input", { type: "text", id: "fo3", maxlength: "200" });
    // Brief 1E-11 (mockup v61): the hours asked (whole days fill 8 hours each, changeable), which
    // of the person's policies it uses or Unpaid, and how it splits.
    const perDay = a.pto?.hoursADay ?? 8;
    const hours = el("input", {
      type: "number",
      id: "fo4",
      min: "0.25",
      step: "0.25",
      inputmode: "decimal",
      value: String(perDay),
    });
    let hoursTouched = false;
    hours.addEventListener("input", () => {
      hoursTouched = true;
      showSplit();
    });
    const days = () => {
      const f = Date.parse(`${from.value}T12:00:00Z`);
      const u = Date.parse(`${until.value || from.value}T12:00:00Z`);
      return Number.isFinite(f) && Number.isFinite(u) && u >= f
        ? Math.round((u - f) / 86_400_000) + 1
        : 1;
    };
    const fill = () => {
      if (!hoursTouched) hours.value = String(days() * perDay);
      showSplit();
    };
    from.addEventListener("change", fill);
    until.addEventListener("change", fill);
    const policies = a.pto?.policies ?? [];
    const firstUsable = policies.find((x) => x.paid && x.usable);
    let use: string = firstUsable?.id ?? "";
    const useSeg = el(
      "div",
      { class: "seg", role: "radiogroup", "aria-label": "Use" },
      ...[
        ...policies.map((x) => ({
          id: x.id,
          label: x.words,
          disabled: x.paid && !x.usable,
        })),
        { id: "", label: "Unpaid", disabled: false },
      ].map((o) => {
        const input = el("input", {
          type: "radio",
          name: "fpto",
          value: o.id,
          checked: o.id === use,
          disabled: o.disabled,
        });
        input.addEventListener("change", () => {
          use = o.id;
          showSplit();
        });
        return el("label", {}, input, o.label);
      }),
    );
    const split = el("span", { class: "small muted", "data-pto-split": "1" });
    const waits = policies
      .filter((x) => x.paid && !x.usable && x.usableWords)
      .map((x) => `${x.name}: ${x.usableWords}.`);
    function showSplit() {
      const asked = Number(hours.value) || 0;
      const chosen = policies.find((x) => x.id === use);
      split.textContent = `${splitLine(asked, chosen && chosen.paid ? chosen.left : null)}. The office sees this before approving.`;
    }
    showSplit();
    const ask = el(
      "button",
      { type: "button", class: "btn sm" },
      icon("paper-airplane"),
      " Request time off",
    );
    ask.addEventListener("click", async () => {
      const r = await api<Avail>("POST", "/api/field/time-off", {
        from: from.value,
        until: until.value || from.value,
        reason: reason.value,
        hours: hours.value,
        policyId: use || null,
      });
      if (!r.ok) return toast("That wasn't sent", r.body.error);
      a = r.body;
      draw();
      toast("Time off requested", "The office approves it");
    });
    const closeBtn = el(
      "button",
      { type: "button", class: "btn ib", "aria-label": "Close", "data-tip": "Close" },
      icon("x"),
    );
    closeBtn.addEventListener("click", close);
    const off = el(
      "section",
      { class: "pcard", "aria-label": "Time off", id: "fa-off" },
      el("h2", { style: "margin:0 0 6px" }, "Time off"),
      ...a.timeOff.map((t) =>
        el(
          "div",
          { class: "prow2" },
          el(
            "span",
            {},
            el("b", {}, t.words),
            t.hours ? el("span", { class: "small muted" }, ` · ${t.hours}`) : "",
            t.reason ? el("span", { class: "small muted" }, ` · ${t.reason}`) : "",
          ),
          chip(
            t.status === "requested"
              ? "Waiting for the office"
              : t.status === "approved"
                ? "Approved"
                : "Declined",
            t.status === "approved" ? "ok" : t.status === "declined" ? "bad" : "info",
          ),
        ),
      ),
      el(
        "div",
        { class: "grid2", style: "gap:8px" },
        el("div", { class: "field" }, el("label", { for: "fo1" }, "From"), from),
        el("div", { class: "field" }, el("label", { for: "fo2" }, "Until"), until),
      ),
      el("div", { class: "field" }, el("label", { for: "fo4" }, "Hours"), hours),
      el(
        "div",
        { class: "field" },
        el("label", {}, "Use"),
        useSeg,
        split,
        ...waits.map((w) => el("span", { class: "small muted" }, w)),
      ),
      el("div", { class: "field" }, el("label", { for: "fo3" }, "Reason (optional)"), reason),
      ask,
    );
    sheet.replaceChildren(
      el("div", { class: "psh-grab" }),
      el(
        "div",
        { class: "cardhead" },
        el("h2", { style: "margin:0" }, "My availability"),
        closeBtn,
      ),
      el(
        "p",
        { class: "small muted", style: "margin:0 0 6px" },
        "Set it once and it repeats every week. The office sees it when scheduling.",
      ),
      ...(a.asked
        ? [el("p", { class: "note", role: "status" }, "The last change is waiting for the office.")]
        : []),
      ...rows,
      el(
        "p",
        { class: "small muted" },
        `Changes within ${a.lockHours} hours of a visit go to the office first.`,
      ),
      save,
      off,
    );
  };
  draw();
  shell.append(scrim, sheet);
  if (focus === "off") sheet.querySelector("#fa-off")?.scrollIntoView();
}

// ---- Documents -----------------------------------------------------------------------------------------------

interface Docs {
  personId: string;
  onFile: { code: string; name: string; state: string; words: string; canUpload: boolean }[];
  needed: { code: string; name: string; words: string; canUpload: boolean }[];
}

async function docsTab(body: HTMLElement) {
  const res = await api<Docs>("GET", "/api/field/documents");
  if (!res.ok) {
    body.append(
      el("p", { class: "note", role: "alert" }, res.body.error ?? "Documents couldn't be loaded."),
    );
    return;
  }
  let d = res.body;
  const slot = el("div", { class: "pcard pcol", "aria-label": "Documents" });
  const upload = (code: string, name: string) => {
    const id = `fd-${code}`;
    const input = el("input", {
      type: "file",
      accept: "image/*,.pdf",
      capture: "environment",
      hidden: true,
      id,
      "aria-label": `Photo of ${name}`,
    });
    input.addEventListener("change", async () => {
      const f = input.files?.[0];
      if (!f) return;
      const up = await uploadFile(f, { kind: "person", id: d.personId });
      if (!up.ok) return toast("That wasn't uploaded", up.error);
      const r = await api<Docs>("POST", `/api/field/documents/${encodeURIComponent(code)}`, {
        fileId: up.file.id,
      });
      if (!r.ok) return toast("That wasn't sent", r.body.error);
      d = r.body;
      draw();
      toast("Uploaded", "The office reviews it");
    });
    return el(
      "span",
      {},
      el("label", { class: "btn sm", for: id }, icon("device-camera"), " Upload or take a photo"),
      input,
    );
  };
  const LEVEL: Record<string, [string, string]> = {
    ok: ["Active", "ok"],
    review: ["In review", "info"],
    rejected: ["Not accepted", "bad"],
    expired: ["Expired", "bad"],
  };
  function draw() {
    slot.replaceChildren(
      ...d.onFile.map((x) => {
        const [w, l] = LEVEL[x.state] ?? ["On file", "plain"];
        return el(
          "div",
          { class: "prow2" },
          el(
            "span",
            {},
            el("b", {}, x.name),
            el("br"),
            el("span", { class: "small muted" }, x.words),
          ),
          el(
            "span",
            { class: "actions" },
            chip(w, l),
            x.canUpload && x.state !== "ok" ? upload(x.code, x.name) : "",
          ),
        );
      }),
      ...d.needed.map((x) =>
        el(
          "div",
          { class: "prow2" },
          el(
            "span",
            {},
            el("b", {}, x.name),
            el("br"),
            el("span", { class: "small muted" }, x.words),
          ),
          el(
            "span",
            { class: "actions" },
            chip("Needed", "warn"),
            x.canUpload ? upload(x.code, x.name) : "",
          ),
        ),
      ),
      ...(d.onFile.length || d.needed.length
        ? []
        : [el("p", { class: "muted" }, "Nothing on file yet.")]),
    );
  }
  draw();
  body.append(slot);
}

// ---- Chat ----------------------------------------------------------------------------------------------------

async function chatTab(body: HTMLElement) {
  const res = await api<{ messages: { mine: boolean; who: string; body: string; at: string }[] }>(
    "GET",
    "/api/field/chat",
  );
  if (!res.ok) {
    body.append(
      el("p", { class: "note", role: "alert" }, res.body.error ?? "Chat couldn't be loaded."),
    );
    return;
  }
  let msgs = res.body.messages;
  const list = el("div", { class: "pchat", role: "list", "aria-label": "Chat with the office" });
  const draw = () =>
    list.replaceChildren(
      ...(msgs.length
        ? msgs.map((m) =>
            el(
              "div",
              { class: `pmsg${m.mine ? " mine" : ""}`, role: "listitem" },
              el("b", {}, m.who),
              el("p", {}, m.body),
              el("span", { class: "small muted" }, m.at),
            ),
          )
        : [el("p", { class: "muted" }, "No messages yet. Write to the office below.")]),
    );
  const box = el("input", {
    type: "text",
    id: "fc-msg",
    maxlength: "1000",
    placeholder: "Write to the office",
  });
  const sendBtn = el(
    "button",
    { type: "button", class: "btn ib", "aria-label": "Send", "data-tip": "Send" },
    icon("paper-airplane"),
  );
  const send = async () => {
    if (!box.value.trim()) return;
    sendBtn.disabled = true;
    const r = await api<{ messages: typeof msgs }>("POST", "/api/field/chat", { body: box.value });
    sendBtn.disabled = false;
    if (!r.ok) return toast("That wasn't sent", r.body.error);
    msgs = r.body.messages;
    box.value = "";
    draw();
    toast("Sent", "The office sees it in their inbox");
  };
  sendBtn.addEventListener("click", () => void send());
  box.addEventListener("keydown", (e) => {
    if (e.key === "Enter") void send();
  });
  draw();
  // Brief 1E-34: opening Chat clears the tab's unread mark.
  void api("POST", "/api/field/chat/seen", {});
  body.append(
    el("div", { class: "pcard pcol" }, list),
    el(
      "div",
      { class: "pcompose" },
      el("label", { for: "fc-msg", class: "sr" }, "Message"),
      box,
      sendBtn,
    ),
  );
}
